import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import {Helmet} from "react-helmet";
import {toast} from "react-toastify";
import moment from "moment";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import API from "../../api";

// Admin "Offer Links" list (MGDWD-201): every generated discount link with its
// status, how many times the page was opened, and who redeemed it. Creating a
// new link lives on a separate page reached via the button below (same
// list -> create pattern as Plans / Create Plan).
function OfferLinks() {
	const navigate = useNavigate();
	const [offers, setOffers] = useState([]);
	const [isLoading, setLoading] = useState(false);

	useEffect(() => {
		setLoading(true);
		API.get("admin/discount-offers")
			.then((response) => setOffers(response.data))
			.finally(() => setLoading(false));
	}, []);

	const copyLink = (url) => {
		navigator.clipboard.writeText(url);
		toast.success("Link copied", {position: toast.POSITION.TOP_RIGHT});
	};

	const linkBody = (offer) => (
		<div className="d-flex align-items-center gap-2">
			<code>{offer.code}</code>
			<Button size="sm" variant="outline-primary" onClick={() => copyLink(offer.url)}>Copy</Button>
		</div>
	);

	const typeBody = (offer) => {
		if (offer.maxRedemptions == null) {
			return <Badge bg="info">Public</Badge>;
		}
		if (offer.maxRedemptions === 1) {
			return <Badge bg="primary">Personal</Badge>;
		}
		return <Badge bg="primary">{`Max ${offer.maxRedemptions}`}</Badge>;
	};

	const statusBody = (offer) => {
		const expired = moment().isAfter(offer.expiresAt);
		const usedUp = offer.maxRedemptions != null && (offer.redemptions?.length || 0) >= offer.maxRedemptions;
		if (expired) {
			return <Badge bg="secondary">Expired</Badge>;
		}
		if (usedUp) {
			return <Badge bg="dark">Used up</Badge>;
		}
		return <Badge bg="success">Active</Badge>;
	};

	const visitsBody = (offer) => {
		if (!offer.visitCount) {
			return "—";
		}
		return `${offer.visitCount}× (first ${moment(offer.visitedAt).format("MMM D, HH:mm")})`;
	};

	const redeemedBody = (offer) => {
		const rs = offer.redemptions || [];
		if (!rs.length) {
			return "—";
		}
		const who = rs.map((r) => r.account?.email || `#${r.accountId}`).join(", ");
		return `${rs.length}× — ${who}`;
	};

	const expiresBody = (offer) => moment(offer.expiresAt).format("MMM D, YYYY HH:mm");
	const createdBody = (offer) => moment(offer.created_at).format("YYYY-MM-DD");

	return (
		<>
			<Helmet>
				<title>Offer Links | MyGolfDNA.com</title>
				<meta name="description" content="Discount offer links"/>
			</Helmet>
			<div className="mt-4">
				<Row className="align-items-center mb-3">
					<Col>
						<h2 className="mb-0">Offer Links</h2>
					</Col>
					<Col xs="auto">
						<Button variant="primary" onClick={() => navigate("/admin/discount-offers/new")}>
							Generate New Link
						</Button>
					</Col>
				</Row>
				<div className="card">
					<DataTable
						value={offers}
						loading={isLoading}
						paginator rows={10}
						rowsPerPageOptions={[10, 25, 50]}
						tableStyle={{minWidth: "60rem"}}
						emptyMessage="No offer links yet."
					>
						<Column header="Link" body={linkBody}/>
						<Column field="email" header="Email"/>
						<Column field="note" header="Note"/>
						<Column header="Type" body={typeBody}/>
						<Column header="Status" body={statusBody}/>
						<Column header="Visits" body={visitsBody}/>
						<Column header="Redeemed (who paid)" body={redeemedBody}/>
						<Column field="expiresAt" header="Expires" body={expiresBody} sortable/>
						<Column field="created_at" header="Created" body={createdBody} sortable/>
					</DataTable>
				</div>
			</div>
		</>
	);
}

export default OfferLinks;
